import { Stack, SxProps, Theme, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import { Link } from 'ui-kit';
import { inheritDataTestId } from 'ui-kit/lib/utils/dataTestId';
import MonitorIcon from 'ui-kit/lib/icons/24/Menu/Monitor';
import TabletIcon from 'ui-kit/lib/icons/24/Menu/Smartphone';
import { useTranslate } from '@/i18n';
import { spreadSx } from 'ui-kit/lib/utils/spreadSx';
import { DeviceClass } from '@/api/__generated__/webApi';

const deviceClassIconMap: Record<DeviceClass, ReactNode> = {
  [DeviceClass.MOBILE]: <TabletIcon />,
  [DeviceClass.DESKTOP]: <MonitorIcon />,
  [DeviceClass.ANONYMIZED]: <TabletIcon />,
  [DeviceClass.TABLET]: <TabletIcon />,
  [DeviceClass.PHONE]: <TabletIcon />,
  [DeviceClass.WATCH]: <TabletIcon />,
  [DeviceClass.AUGMENTED_REALITY]: <TabletIcon />,
  [DeviceClass.VIRTUAL_REALITY]: <TabletIcon />,
  [DeviceClass.E_READER]: <TabletIcon />,
  [DeviceClass.SET_TOP_BOX]: <MonitorIcon />,
  [DeviceClass.TV]: <MonitorIcon />,
  [DeviceClass.GAME_CONSOLE]: <TabletIcon />,
  [DeviceClass.HANDHELD_GAME_CONSOLE]: <TabletIcon />,
  [DeviceClass.HOME_APPLIANCE]: <TabletIcon />,
  [DeviceClass.VOICE]: <TabletIcon />,
  [DeviceClass.SMART_DISPLAY]: <MonitorIcon />,
  [DeviceClass.CAR]: <TabletIcon />,
  [DeviceClass.CLOUD]: <TabletIcon />,
  [DeviceClass.ROBOT]: <MonitorIcon />,
  [DeviceClass.ROBOT_MOBILE]: <TabletIcon />,
  [DeviceClass.ROBOT_IMITATOR]: <TabletIcon />,
  [DeviceClass.HACKER]: <MonitorIcon />,
  [DeviceClass.UNKNOWN]: <MonitorIcon />,
  [DeviceClass.UNCLASSIFIED]: <MonitorIcon />,
};

export interface SessionBlockProps {
  title: string;
  subtitle?: string;
  iconType: DeviceClass;
  current?: boolean;
  onEndSession?: () => void;
  sx?: SxProps<Theme>;
}
export const SessionBlock = ({
  title,
  subtitle,
  current,
  iconType,
  onEndSession,
  sx,
  ...props
}: SessionBlockProps) => {
  const { t } = useTranslate('profile');
  return (
    <Stack
      sx={[styles.container, ...spreadSx(sx)]}
      data-test-id={inheritDataTestId(props, 'container')}
    >
      <Stack direction={'row'} sx={styles.content}>
        <Stack sx={styles.iconBlock}>{deviceClassIconMap[iconType]}</Stack>
        <Stack sx={styles.details}>
          <Stack minWidth={100}>
            <Typography sx={styles.title}>{title}</Typography>
          </Stack>
          {subtitle && (
            <Stack>
              <Typography sx={{ typography: { xs: 'string/sm' } }}>
                {subtitle}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack>
        {current ? (
          <Typography
            sx={{
              typography: { xs: 'string/md' },
              color: (t) => t.palette.content.additional.default,
            }}
          >
            {t('ACTIVE_NOW')}
          </Typography>
        ) : (
          <Link
            data-test-id={inheritDataTestId(props, 'end-session-link')}
            color={'secondary'}
            onClick={onEndSession}
            sx={{ typography: { xs: 'link/md' } }}
          >
            {t('END_SESSION')}
          </Link>
        )}
      </Stack>
    </Stack>
  );
};

const styles = {
  container: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: (t) => t.palette.card.secondary.border.default,
    paddingY: 18,
    paddingX: 24,
    gap: 18,
    // layout part
    maxWidth: { xs: 'initial', sm: 476 },
    minWidth: 200,
  },
  iconBlock: {},
  details: {
    minWidth: 0,
  },
  content: {
    gap: 12,
  },
  title: {
    typography: { xs: 'label/md', md: 'label/lg' },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 100,
  },
} satisfies Record<string, SxProps<Theme>>;
